import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Input from 'components/atoms/Input'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim } from 'lodash'

const AddColorModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    color: '',
    hex: '#000000',
    active: true,
    colorAr: '',
  })

  useEffect(() => {
    if (id) {
      fetchColorData()
    }
  }, [id])

  const fetchColorData = async () => {
    try {
      const response = await apiGet(`color/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          color: data.color || '',
          colorAr: data.colorAr || '',
          hex: data.hex || '#000000',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addColorModal.messages.failedToFetchColorData'))
      }
    } catch (error) {
      console.error('Error fetching color data:', error)
      notify.error(t('addColorModal.messages.failedToFetchColorData'))
    }
  }

  const colorNameToHex = color => {
    const dummy = document.createElement('div')
    dummy.style.color = color
    document.body.appendChild(dummy)

    const computedColor = window.getComputedStyle(dummy).color
    document.body.removeChild(dummy)
    // eslint-disable-next-line
    if (!computedColor || (computedColor === 'rgb(0, 0, 0)' && color.toLowerCase() !== 'black')) return null

    const rgb = computedColor.match(/\d+/g)
    if (!rgb) return null

    return `#${((1 << 24) + (parseInt(rgb[0]) << 16) + (parseInt(rgb[1]) << 8) + parseInt(rgb[2])).toString(16).slice(1)}`
  }

  const handleChange = e => {
    const { name, value } = e.target
    const hexValue = colorNameToHex(value)

    setFormData(prevData => ({
      ...prevData,
      [name]: value,
      hex: hexValue || prevData.hex,
    }))
  }

  const handleHexChange = e => {
    const { value } = e.target
    setFormData(prevData => ({
      ...prevData,
      hex: value,
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.color)) {
      notify.error(t('addColorModal.messages.colorNameRequired'))
      return
    }
    if (!trim(formData.colorAr)) {
      notify.error(t('addColorModal.messages.colorArabicNameRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`color/${id}`, formData)
        if (response.success) {
          notify.success(t('addColorModal.messages.colorUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('color', formData)
        if (response.success) {
          notify.success(t('addColorModal.messages.colorAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        notify.error(response.message || t('addColorModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving color:', error)
      notify.error(t('addColorModal.messages.failedToSaveColor'))
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      {/* Vehicle Color Name */}
      <div>
        <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addColorModal.labels.vehicleColor')}
        </label>
        <input
          type="text"
          name="color"
          value={formData.color}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none 
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addColorModal.placeholders.enterVehicleColor')}
        />
      </div>

      {/* Vehicle Arabic Color Name */}
      <div>
        <label htmlFor="colorAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addColorModal.labels.vehicleArabicColor')}
        </label>
        <input
          type="text"
          name="colorAr"
          value={formData.colorAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none 
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addColorModal.placeholders.enterVehicleColorArabic')}
        />
      </div>

      {/* Hex Picker */}
      <div>
        <label htmlFor="hex" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addColorModal.labels.colorHex') || 'Color Hex'}
        </label>
        <input
          type="color"
          name="hex"
          value={formData.hex}
          onChange={handleHexChange}
          className="block w-full h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none 
                     focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Active Switch */}
      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addColorModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium 
                     text-gray-700 bg-white hover:bg-gray-50 focus:outline-none 
                     focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addColorModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium 
                     text-white bg-dark-600 hover:bg-dark-700 focus:outline-none 
                     focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addColorModal.buttons.updateColor') : t('addColorModal.buttons.addColor')}
        </button>
      </div>
    </div>
  )
}

export default AddColorModal
